import { inv } from 'mathjs';

// llh => xyz
export function gd2xyz(lat, lon, h) {
  try {
    const a = 6378137.0;
    const f = parseFloat(1 / 298.257223563);
    const b = a * (1 - f);

    const lat_rad = parseFloat(lat) * Math.PI / 180;
    const lon_rad = parseFloat(lon) * Math.PI / 180;

    const N = a ** 2 / Math.sqrt(a ** 2 * Math.cos(lat_rad) ** 2 + b ** 2 * Math.sin(lat_rad) ** 2);
    const eSQ = (a ** 2 - b ** 2) / a ** 2;

    const X = (parseFloat(N + parseFloat(h)) * Math.cos(lat_rad) * Math.cos(lon_rad));
    const Y = (parseFloat(N + parseFloat(h)) * Math.cos(lat_rad) * Math.sin(lon_rad));
    const Z = (parseFloat(N - N * eSQ + parseFloat(h)) * Math.sin(lat_rad));

    return [X, Y, Z];
  } catch (error) {
    return [0, 0, 0];
  }
}

// xyz => llh
export function xyz2gd(x, y, z) {
  try {
    x = parseFloat(x);
    y = parseFloat(y);
    z = parseFloat(z);

    const a = 6378137.0;
    const f = parseFloat(1 / 298.257223563);
    const b = a * (1 - f);

    const eSQ = (a ** 2 - b ** 2) / a ** 2;

    let lon = Math.atan2(parseFloat(y), parseFloat(x)) * 180 / Math.PI;
    if (lon > 180) lon -= 360;
    if (lon < -180) lon += 360;

    const p = Math.sqrt(x ** 2 + y ** 2);
    let Phi0 = Math.atan2(parseFloat(z) * inv(1 - eSQ), p);

    let q = 0;
    let Phi = 0;
    let h = 0;

    while (q !== 1) {
      let N0 = a ** 2 / Math.sqrt(a ** 2 * Math.cos(Phi0) ** 2 + b ** 2 * Math.sin(Phi0) ** 2);
      h = p / Math.cos(Phi0) - N0;
      Phi = Math.atan2(parseFloat(z) * inv(1 - eSQ * (N0 / (N0 + h))), p);

      if (Math.abs(Phi - Phi0) <= 1e-13) break;
      Phi0 = Phi;
    }

    const lat = Phi * 180 / Math.PI;
    return [lat, lon, h];
  } catch (error) {
    return [0, 0, 0];
  }
}

// llh(degree) => llh(dms)
export function degree2dms(lat, lng) {
  try {
    const convertToDMS = (deg) => {
      const degree = Math.floor(parseFloat(deg));
      const min = Math.floor((parseFloat(deg) - degree) * 60);
      const sec = ((parseFloat(deg) - degree - min / 60) * 3600).toFixed(5);
      return [degree, min, sec];
    };

    return {
      lat: convertToDMS(parseFloat(lat)),
      lng: convertToDMS(parseFloat(lng)),
    };
  } catch (error) {
    return {
      lat: [0, 0, 0],
      lng: [0, 0, 0],
    };
  }
}

// llh(dms) => llh(degree)
export function dms2degree(latDMS, lngDMS) {
  try {
    const convertToDegree = (dms) => parseFloat(dms[0]) + parseFloat(dms[1]) / 60.0 + parseFloat(dms[2]) / 3600;
    return {
      lat: convertToDegree(latDMS),
      lng: convertToDegree(lngDMS),
    };
  } catch (error) {
    return { lat: 0, lng: 0 };
  }
}

// xyz => neu (Topocentric coordinates)
export function xyz2topo(x, y, z, lat, lon) {
  x = parseFloat(x);
  y = parseFloat(y);
  z = parseFloat(z);
  lat = parseFloat(lat);
  lon = parseFloat(lon);

  const lat_rad = lat * Math.PI / 180;
  const lon_rad = lon * Math.PI / 180;
  const sin_lat = Math.sin(lat_rad);
  const cos_lat = Math.cos(lat_rad);
  const sin_lon = Math.sin(lon_rad);
  const cos_lon = Math.cos(lon_rad);

  const north = -sin_lat * cos_lon * x - sin_lat * sin_lon * y + cos_lat * z;
  const east = -sin_lon * x + cos_lon * y;
  const up = cos_lat * cos_lon * x + cos_lat * sin_lon * y + sin_lat * z;

  return [north, east, up];
}
